import { Switcher } from '@lib/components'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import type React from 'react'
import { useState } from 'react'
import { useSelector } from 'react-redux'

import { SHARE_PERMISSION } from '@/permissions/project/definitions'
import { clsJoin } from '@/utils'
import { useProjectAccess, useProjectPermissions } from '../../../../hooks'
import {
  Access,
  Embed,
  Export,
  ExportTutorial,
  ShareTutorial,
} from './components'
import css from './styles.module.css'

export type ShareSettingsOption = {
  label: string
  value: string
  visible: boolean
  selected?: boolean
  disabled?: boolean
  badgeType?: string | null
  icon?: React.ReactElement
}

export enum LinkPermissionOptionLabels {
  CAN_VIEW = 'view',
  CAN_COMMENT = 'comment',
  CAN_EDIT = 'edit',
}

type Props = {
  activeTab?: Tab
  closeButton: React.ReactNode
  // Opened off the canvas (from the dashboard): Embed and Export need it
  accessOnly?: boolean
}

export enum Tab {
  Access,
  Embed,
  Export,
}

export const Share: React.FC<Props> = (props) => {
  // The editor and the report screen work out what this user may do with the
  // project; opened from the dashboard, nothing has, and every control would
  // read as not allowed. Same inputs, same result on the canvas.
  useProjectPermissions()

  const isReadOnly = useSelector(
    migrationProjectSelector.getProjectLegacyAccessTypeIsReadOnly
  )

  const { hasAccess } = useProjectAccess(SHARE_PERMISSION.ACCESS_TAB)

  // A report is shared, not exported or embedded: its own header already
  // carries the download menu, and Embed frames a canvas a report does not
  // have. The dialog is the Access tab and nothing else.
  const isReport = useSelector(migrationProjectSelector.getProjectIsReport)
  const { accessOnly = false } = props
  const onlyAccess = isReport || accessOnly

  const hideAccess = isReadOnly || !hasAccess

  // Tab.Access is 0: `props.activeTab ||` dropped it, and the tab was then
  // picked by whether access was known at the first render. The asked tab is
  // kept, and Access falls back to Export only while it is not available.
  const [activeTab, setActiveTab] = useState(props.activeTab ?? Tab.Access)

  const tab = hideAccess && activeTab === Tab.Access ? Tab.Export : activeTab

  const showAccess = onlyAccess || (!hideAccess && tab === Tab.Access)
  const showExport = tab === Tab.Export
  const showEmbed = tab === Tab.Embed

  const hideShareTutorial = showExport || showEmbed
  const hideExportTutorial = showAccess || showEmbed

  return (
    <div className={css.container}>
      <div className={css.header}>
        <div className={css.title}>Share</div>
        <div className={css.close}>{props.closeButton}</div>
      </div>
      <div className={clsJoin(css.body, onlyAccess && css.body_report)}>
        {!onlyAccess && (
          <div className={clsJoin(css.switcher, hideAccess && css.readOnly)}>
            <Switcher
              size="medium"
              // TODO: Fix the type of index
              onClick={(index) => setActiveTab(hideAccess ? index + 1 : index)}
              items={[
                {
                  content: 'Access',
                  checked: tab === Tab.Access,
                  disabled: hideAccess,
                },
                {
                  content: 'Embed',
                  checked: showEmbed,
                },
                {
                  content: 'Export',
                  checked: showExport,
                },
              ]}
            />
          </div>
        )}
        <>
          {showAccess && <Access />}
          {!onlyAccess && showEmbed && <Embed />}
          {!onlyAccess && showExport && <Export />}
        </>
      </div>

      <ShareTutorial hide={onlyAccess || hideShareTutorial} />

      <ExportTutorial hide={onlyAccess || hideExportTutorial} />
    </div>
  )
}
